import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FormEditionDecisionInput } from "@duralex/shared";
import {
  checkAllForms,
  checkForm,
  decideEdition,
  fetchAffectedPackages,
  fetchEditionChanges,
  fetchFormEditions,
  fetchForms,
  redownloadForms,
} from "../api/forms";

export function useForms() {
  return useQuery({ queryKey: ["forms"], queryFn: fetchForms, staleTime: 15_000 });
}

/** The chronology and the affected list belong to an opened row: they mount with it. */
export function useFormEditions(formId: string) {
  return useQuery({
    queryKey: ["forms", formId, "editions"],
    queryFn: () => fetchFormEditions(formId),
    staleTime: 15_000,
  });
}

export function useAffectedPackages(formId: string) {
  return useQuery({
    queryKey: ["forms", formId, "affected"],
    queryFn: () => fetchAffectedPackages(formId),
    staleTime: 15_000,
  });
}

/** The dialog's content: it mounts with the dialog, so the read waits for the opening. */
export function useEditionChanges(formId: string, editionKey: string) {
  return useQuery({
    queryKey: ["forms", formId, "editions", editionKey, "changes"],
    queryFn: () => fetchEditionChanges(formId, editionKey),
    staleTime: 15_000,
  });
}

/** A decision changes the chronology and the Integrations pill, whose
 *  action is the undecided edition; a refused one (somebody decided
 *  meanwhile) reloads what they recorded. */
export function useFormDecision(formId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { editionKey: string; body: FormEditionDecisionInput }) =>
      decideEdition(formId, input.editionKey, input.body),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["forms"] }),
        queryClient.invalidateQueries({ queryKey: ["integrations", "status"] }),
      ]);
    },
  });
}

/** The admin's three actions; a check that came back refreshes the catalogue
 *  and the Integrations pill, whose action is an edition the check found. */
export function useFormActions() {
  const queryClient = useQueryClient();
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["forms"] }),
      queryClient.invalidateQueries({ queryKey: ["integrations", "status"] }),
    ]);
  const checkOne = useMutation({ mutationFn: checkForm, onSuccess: refresh });
  const checkAll = useMutation({ mutationFn: checkAllForms, onSuccess: refresh });
  const redownload = useMutation({ mutationFn: redownloadForms });
  return { checkOne, checkAll, redownload };
}
